import { useEffect, useMemo, useRef } from 'react';
import { Link2, FileText, Sparkles, Star, X, Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useData } from '@/context/DataContext';
import { useApp } from '@/context/AppContext';

interface GettingStartedChecklistProps {
  onDismiss: () => void;
}

const CHECKLIST_ITEMS = [
  { id: 'add-link', label: 'Save your first link', icon: Link2 },
  { id: 'create-note', label: 'Create a note', icon: FileText },
  { id: 'try-ai', label: 'Try the AI assistant', icon: Sparkles },
  { id: 'star-favorite', label: 'Star a favorite', icon: Star },
] as const;

export function GettingStartedChecklist({ onDismiss }: GettingStartedChecklistProps) {
  const { items, allItems, config, updateOnboarding } = useData();
  const { isAIPanelOpen } = useApp();

  const persistedSteps = useMemo(
    () => config.onboarding?.completedSteps ?? [],
    [config.onboarding?.completedSteps]
  );

  // Compute completed steps from current data
  const effectiveCompleted = useMemo(() => {
    const detected: string[] = [];
    if (items.links.length > 0) detected.push('add-link');
    if (items.notes.length > 0) detected.push('create-note');
    if (persistedSteps.includes('try-ai')) detected.push('try-ai');
    if (allItems.some((item) => item.isFavorite)) detected.push('star-favorite');
    return [...new Set([...persistedSteps, ...detected])];
  }, [items.links.length, items.notes.length, allItems, persistedSteps]);

  // Track AI panel open
  useEffect(() => {
    if (isAIPanelOpen && !persistedSteps.includes('try-ai')) {
      updateOnboarding({ completedSteps: [...persistedSteps, 'try-ai'] });
    }
  }, [isAIPanelOpen, persistedSteps, updateOnboarding]);

  // Persist newly detected steps
  const completedKey = useMemo(
    () => [...effectiveCompleted].sort().join(','),
    [effectiveCompleted]
  );
  const persistedKey = useMemo(() => [...persistedSteps].sort().join(','), [persistedSteps]);

  useEffect(() => {
    if (completedKey !== persistedKey) {
      updateOnboarding({ completedSteps: effectiveCompleted });
    }
  }, [completedKey, persistedKey, effectiveCompleted, updateOnboarding]);

  // Auto-dismiss when all complete
  const allComplete = CHECKLIST_ITEMS.every((item) => effectiveCompleted.includes(item.id));
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!allComplete) return;
    dismissTimerRef.current = setTimeout(onDismiss, 1500);
    return () => clearTimeout(dismissTimerRef.current);
  }, [allComplete, onDismiss]);

  const completedCount = CHECKLIST_ITEMS.filter((item) =>
    effectiveCompleted.includes(item.id)
  ).length;

  return (
    <div className="mb-6 bg-brand-50 border border-brand-200 rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Getting Started</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {completedCount} of {CHECKLIST_ITEMS.length} complete
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss getting started checklist"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-brand-100 rounded-full mb-4">
        <div
          className="h-full bg-brand-600 rounded-full transition-all duration-500"
          style={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }}
        />
      </div>

      {/* Checklist items */}
      <div className="space-y-2">
        {CHECKLIST_ITEMS.map((item) => {
          const isComplete = effectiveCompleted.includes(item.id);
          const Icon = item.icon;

          return (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-3 py-1.5 transition-opacity duration-300',
                isComplete && 'opacity-50'
              )}
            >
              {/* Check circle */}
              <div
                className={cn(
                  'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300',
                  isComplete ? 'bg-green-500 border-green-500 animate-check-pop' : 'border-gray-300'
                )}
              >
                {isComplete && <Check className="h-3 w-3 text-white" />}
              </div>

              {/* Icon + label */}
              <Icon
                className={cn(
                  'h-4 w-4 flex-shrink-0',
                  isComplete ? 'text-gray-400' : 'text-gray-500'
                )}
              />
              <span
                className={cn(
                  'text-sm transition-all duration-300',
                  isComplete ? 'text-gray-400 line-through' : 'text-gray-700'
                )}
              >
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
